import { FC } from 'react';
import {
  Card,
  Checkbox,
  Input,
  InputError,
  InputSelect,
  Label,
} from '@features/ui';
import { Controller } from 'react-hook-form';
import {
  roleCheckboxData,
  rolePermissionCheckboxData,
} from '@features/admin/settings/settingData.ts';
import { UserRole, UserRolePermission } from '@src/types/settings.ts';

interface UserRolesProps {
  method: any;
  data?: any;
}
export const UserRoles: FC<UserRolesProps> = ({ method, data }) => {
  const {
    control,
    watch,
    formState: { errors },
  } = method;

  const role = watch('role');

  const roleDefaultPermissions: Record<string, string[]> = {
    [UserRole.SUPPORT_ENTRY]: [
      UserRolePermission.VIEW_TRADER_BASIC,
      UserRolePermission.VIEW_ACCOUNT_BASIC,
      UserRolePermission.ADD_NOTE,
    ],
    [UserRole.SUPPORT_JUNIOR]: [
      UserRolePermission.VIEW_TRADER_BASIC,
      UserRolePermission.VIEW_ACCOUNT_BASIC,
      UserRolePermission.ADD_NOTE,
      UserRolePermission.VIEW_ACCOUNT_FULL,
      UserRolePermission.VIEW_PURCHASE_HISTORY,
      UserRolePermission.VIEW_RECEIPTS,
      UserRolePermission.EDIT_OWN_NOTE,
      UserRolePermission.CREATE_DISCOUNT_CODE,
      UserRolePermission.EDIT_TRADES_AS_COMPANY,
      UserRolePermission.CLOSE_ACCOUNT,
      UserRolePermission.ASSIGN_ACCOUNT,
    ],
    [UserRole.SUPPORT_SENIOR]: [
      UserRolePermission.VIEW_TRADER_BASIC,
      UserRolePermission.VIEW_ACCOUNT_BASIC,
      UserRolePermission.ADD_NOTE,
      UserRolePermission.VIEW_ACCOUNT_FULL,
      UserRolePermission.VIEW_PURCHASE_HISTORY,
      UserRolePermission.VIEW_RECEIPTS,
      UserRolePermission.EDIT_OWN_NOTE,
      UserRolePermission.CREATE_DISCOUNT_CODE,
      UserRolePermission.EDIT_TRADES_AS_COMPANY,
      UserRolePermission.CLOSE_ACCOUNT,
      UserRolePermission.ASSIGN_ACCOUNT,
      UserRolePermission.VIEW_TRADER_SENSITIVE,
      UserRolePermission.EDIT_ACCOUNT,
      UserRolePermission.EDIT_ANY_NOTE,
      UserRolePermission.DELETE_NOTE,
      UserRolePermission.FLAG_TRADER,
      UserRolePermission.DELETE_TRADER,
      UserRolePermission.DISABLE_TRADER,
      UserRolePermission.ADD_PACKAGE,
    ],
    [UserRole.MARKETING_CONTENT]: [UserRolePermission.EDIT_EMAIL_TEMPLATES],
    [UserRole.MARKETING_DATA]: [
      UserRolePermission.VIEW_REPORTS,
      UserRolePermission.EXPORT_REPORTS,
    ],
    [UserRole.ADMIN]: [
      UserRolePermission.VIEW_TRADER_BASIC,
      UserRolePermission.VIEW_TRADER_SENSITIVE,
      UserRolePermission.EDIT_TRADER_PROFILE,
      UserRolePermission.FLAG_TRADER,
      UserRolePermission.DELETE_TRADER,
      UserRolePermission.DISABLE_TRADER,
      UserRolePermission.VIEW_ACCOUNT_BASIC,
      UserRolePermission.VIEW_ACCOUNT_FULL,
      UserRolePermission.EDIT_ACCOUNT,
      UserRolePermission.CLOSE_ACCOUNT,
      UserRolePermission.ASSIGN_ACCOUNT,
      UserRolePermission.CREDIT_REFUND_ACCOUNT,
      UserRolePermission.VIEW_LEELOO_PAID,
      UserRolePermission.VIEW_TRADER_PAID,
      UserRolePermission.ADD_PACKAGE,
      UserRolePermission.VIEW_PURCHASE_HISTORY,
      UserRolePermission.VIEW_RECEIPTS,
      UserRolePermission.VIEW_PAYOUTS,
      UserRolePermission.VIEW_REVENUE,
      UserRolePermission.APPROVE_PAYOUT,
      UserRolePermission.EXECUTE_PAYOUT,
      UserRolePermission.ADD_NOTE,
      UserRolePermission.EDIT_OWN_NOTE,
      UserRolePermission.EDIT_ANY_NOTE,
      UserRolePermission.DELETE_NOTE,
      UserRolePermission.EDIT_TRADES_AS_COMPANY,
      UserRolePermission.CREATE_DISCOUNT_CODE,
      UserRolePermission.MANAGE_USERS,
      UserRolePermission.MANAGE_ROLES,
      UserRolePermission.MANAGE_SYSTEM_SETTINGS,
      UserRolePermission.VIEW_REPORTS,
      UserRolePermission.EXPORT_REPORTS,
      UserRolePermission.EDIT_EMAIL_TEMPLATES,
      UserRolePermission.APPROVE_EVAL,
      UserRolePermission.DENY_EVAL,
      UserRolePermission.BLOCK_EVAL,
    ],
  };

  return (
    <>
      <Card className={'text-xs pb-20 w-full'}>
        <div className="w-full">
          <div className={'flex w-full gap-5'}>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <div className="flex flex-1 flex-col gap-y-2.5 mb-10">
                  <Label className={'ml-0'} htmlFor={field.name} size={'xs'}>
                    Grant user access to areas of the system
                  </Label>
                  <div>
                    <InputSelect
                      className={'text-xs'}
                      error={!!errors.role?.message}
                      options={roleCheckboxData}
                      placeholder="Select"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    {errors.role?.message && (
                      <InputError>{errors.role?.message}</InputError>
                    )}
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="permissions"
              render={({ field }) => {
                // Only show permissions for the selected role
                const availablePermissions = roleDefaultPermissions[role] || [];

                return (
                  <div className="flex flex-1 flex-col gap-y-2.5 mb-10">
                    <Label className={'ml-0'} htmlFor={field.name} size={'xs'}>
                      Permissions
                    </Label>
                    <div className={'flex flex-col gap-y-3'}>
                      {rolePermissionCheckboxData
                        .filter((item) =>
                          availablePermissions.includes(item.value)
                        )
                        .map((item) => (
                          <Checkbox
                            key={item.value}
                            checked={
                              Array.isArray(field.value)
                                ? field.value.includes(item.value)
                                : false
                            }
                            className="w-[15px] h-[14px] border-2 border-white"
                            id={`checkbox-permissions-${item.value}`}
                            label={item.label}
                            onCheckedChange={(checked) => {
                              const value = item.value;
                              let newValues = Array.isArray(field.value)
                                ? [...field.value]
                                : [];

                              if (checked) {
                                if (!newValues.includes(value))
                                  newValues.push(value);
                              } else {
                                newValues = newValues.filter(
                                  (v) => v !== value
                                );
                              }
                              field.onChange(newValues);
                            }}
                          />
                        ))}
                    </div>
                  </div>
                );
              }}
            />
          </div>

          <div className={'flex w-full gap-5'}>
            <Controller
              control={control}
              name="monthly_reset_allowed"
              render={({ field }) => (
                <div className="flex flex-1 flex-col gap-y-2.5 mb-6">
                  <Label htmlFor={field.name} size={'xs'}>
                    Monthly Resets Allowed
                  </Label>
                  <div className={'flex flex-col gap-y-2'}>
                    <Input
                      {...field}
                      id={field.name}
                      placeholder={'0'}
                      textSize={'xs'}
                      type={'number'}
                    />
                    <Label
                      className={'ml-5'}
                      size={'xs'}
                    >{`Used this month : ${data?.monthlyNewUsedThisMonth ?? 0}`}</Label>
                  </div>
                </div>
              )}
            />
            <div className={'flex-1'} />
          </div>
          <div className={'flex w-full gap-5'}>
            <Controller
              control={control}
              name="monthly_new_accounts_allowed"
              render={({ field }) => (
                <div className="flex flex-1 flex-col gap-y-2.5 mb-6">
                  <Label htmlFor={field.name} size={'xs'}>
                    Monthly New Accounts Allowed
                  </Label>
                  <div className={'flex flex-col gap-y-2'}>
                    <Input
                      {...field}
                      id={field.name}
                      placeholder={'0'}
                      textSize={'xs'}
                      type={'number'}
                    />
                    <Label
                      className={'ml-5'}
                      size={'xs'}
                    >{`Used this month : ${data?.monthlyResetUsedThisMonth ?? 0}`}</Label>
                  </div>
                </div>
              )}
            />
            <div className={'flex-1'} />
          </div>
        </div>
      </Card>
    </>
  );
};
